import {
  PlayerApi,
  PlayerControlsApi,
  PlayerProps,
} from '@accedo/one-plugin-sdk';
import { proxy, ref, snapshot, useSnapshot } from 'valtio';

import { Item, ScheduleResponse, ScheduleType } from '#/client/models';
import { controlsConfig } from '#/components/PlayerControls/config';
import { devtoolsApi } from '#/dev/devtools';
import { PlaybackError } from '#/errors/serviceErrors';
import { PlaybackDetails } from '#/hooks/playback/utils';
import {
  MarkerCue,
  MarkerCueType,
  PlayerFeatures,
} from '#/service/features/playback/types';
import { ignoreError } from '#/service/utils';

import { adsActions, adsStore } from '../ads/ads.store';
import { resetStore } from '../common';
// We are aware of this circular dependency, we intentionally disable eslint here until we fix it.
// eslint-disable-next-line import/no-cycle
import { controlsActions } from '../controls/controls.store';
import { tb } from '../translation/translation.store';

export enum SeekDirection {
  Forward = 'forward',
  Rewind = 'rewind',
}

export interface PlayerStore {
  adsLoaded: boolean;
  apiReady: boolean;
  container: HTMLElement | null;
  loaded: boolean;
  /** Storing a value of ads plugin state here because we are not passing down the full ads store
   * to the player plugins. The value of the adsLoaded will keep the same value at all the time
   * as playerStore.adsLoaded.
   * It is being updated in the PlaybackPage to avoid circular dependency between the two stores.
   * Storing the timestamp when a user last interacted with the player to keep track of inactivity */
  lastInteraction: number;
  mvpdContainer: HTMLElement | null;
  player: HTMLVideoElement | null;
  playerName: string;
  playerResources: {
    advertisements?: PlaybackDetails['advertisements'];
    /** This is selected from `playerFeatures` (video) and `audioPlayerFeatures` based on subtype. */
    playerFeatures?: PlayerFeatures;
    poster?: PlayerProps['poster'];
    resource?: Item;
  };
  /** @depracated, remove after all integrations are updated */
  shouldAutoPlay: boolean;
  /** are we playing a live stream? */
  isLive: boolean;
  /** are we at the "live edge", meaning we're playing the latest chunk, and we haven't seeked back in DVR mode. */
  isAtLiveEdge: boolean;
  /** can we seek in the video? meaning VOD or Live DVR, with trickPlay enabled. */
  canSeek: boolean;
  /** marker cues for the skip intro button */
  cues: MarkerCue[];
  /** the parental pin that was entered by the user. not cleared on reset, only on navigation to non-playback pages. */
  enteredParentalPin: string | null;
  enteredParentalPinCount: number;
  /** the current bitrate of the video, in bps, if the player reports it. */
  bitrate?: number;
  /** the current video resolution. set once on play. the player might keep updating it as the quality level changes. */
  videoWidth?: number;
  videoHeight?: number;
  /** the current segment based on the cues */
  currentSegment?: MarkerCueType;
  /** the actual live edge can be a bit behind the seekRange.end in practice, this is our own estimate */
  liveEdge?: number;
  pluginApi?: PlayerApi;
  controlsApi?: PlayerControlsApi;
  /** information about the current channel during live playback */
  channel: {
    schedule?: {
      channelNumber: number;
      from: string;
      until: string;
      response: ScheduleResponse;
    };
    currentProgram?: ScheduleType;
    /** Timestamp of the last completed seek event */
    lastSeek?: Date;
  };
  /** sessionGuid of the /play request which CORE uses to decide what is a new session */
  playbackSessionId?: string;
}

export const playerStore = proxy<PlayerStore>({
  adsLoaded: false,
  apiReady: false,
  container: null,
  loaded: false,
  lastInteraction: Date.now(),
  mvpdContainer: null,
  player: null,
  playerName: '',
  playerResources: {},
  shouldAutoPlay: false,
  isLive: false,
  isAtLiveEdge: false,
  canSeek: true,
  cues: [],
  enteredParentalPin: null,
  enteredParentalPinCount: 0,
  bitrate: undefined,
  videoWidth: undefined,
  videoHeight: undefined,
  currentSegment: undefined,
  liveEdge: undefined,
  pluginApi: undefined,
  controlsApi: undefined,
  channel: {},
  playbackSessionId: undefined,
});

export const initialPlayerStore = snapshot(playerStore) as PlayerStore;

const canSeek = () => {
  const trickPlayEnabled =
    playerStore.playerResources?.playerFeatures?.trickPlay ?? true;
  // we can seek in VOD or DVR, but not in LIVE
  const { start = 0, end = 0 } = playerStore.controlsApi?.getSeekRange() || {};

  return trickPlayEnabled && end > start && !adsStore.isInsideAd;
};

export const playerActions = {
  setPluginApi(api: PlayerApi) {
    playerStore.pluginApi = api;
  },
  setLastInteraction() {
    playerStore.lastInteraction = Date.now();
  },
  play() {
    playerStore.pluginApi?.play?.()?.catch?.((error) => {
      controlsActions.setShowPlayer(true);
      controlsActions.setShowSpinner(false);
      ignoreError(error);
    });
    // set initial video resolution
    playerStore.videoWidth = playerStore.player?.videoWidth;
    playerStore.videoHeight = playerStore.player?.videoHeight;
  },
  seekWithKeyboard(time?: number) {
    if (!(canSeek() && time)) {
      return;
    }

    if (playerStore.player) {
      playerStore.player.currentTime = time;
    }
  },
  seekToDirection(direction: SeekDirection) {
    // ignore if the media is not loaded yet, or seeking is disabled
    if (!playerStore.player?.duration || !canSeek()) {
      return;
    }

    playerStore.player.currentTime +=
      direction === SeekDirection.Forward
        ? controlsConfig.seekTime
        : -controlsConfig.seekTime;
  },
  setLastSeek(time: Date) {
    playerStore.channel.lastSeek = time;
  },
  setPlayerContainerStyles() {
    if (!playerStore.container) {
      return;
    }
    playerStore.container.style.height = '100%';
    playerStore.container.style.display = 'flex';
    playerStore.container.style.position = 'relative';
    playerStore.container.style.top = '0';
    playerStore.container.style.left = '0';
  },
  setPlayerContext(playerContext: PlayerStore['playerResources']) {
    playerStore.playerResources = playerContext;
  },
  setCues(cues?: MarkerCue[]) {
    playerStore.cues = cues ?? [];
  },
  setCurrentSegment(segment: MarkerCueType | undefined) {
    playerStore.currentSegment = segment;
  },
  setEnteredParentalPin(pin: string | null) {
    playerStore.enteredParentalPin = pin;
  },
  setEnteredParentalPinCount(count: number) {
    playerStore.enteredParentalPinCount = count;
  },
  setShouldAutoPlay(value: boolean) {
    playerStore.shouldAutoPlay = value;
  },
  setPlayerName(name: string) {
    playerStore.playerName = name;
  },
  setPlayer(player: HTMLVideoElement | null) {
    if (player && player !== playerStore.player) {
      playerStore.player = ref(player);
      adsActions.assignVideoPlayer(player);

      if (playerStore.player.muted) {
        controlsActions.setVolume(0);
      } else {
        controlsActions.setVolume(playerStore.player?.volume || 0);
      }
    }
  },
  setPlayerApiReady(isReady: boolean) {
    playerStore.apiReady = isReady;
  },
  setAdsLoaded(loaded: boolean) {
    playerStore.adsLoaded = loaded;
  },
  setLoaded(loaded: boolean) {
    playerStore.loaded = loaded;
  },
  setContainer(container: HTMLElement | null) {
    if (container && container !== playerStore.container) {
      playerStore.container = ref(container);
      adsActions.assignAdWrapper(container);
    }
  },
  reloadCaptions() {
    controlsActions.updateSubtitleTracks(true);
  },
  onControlsCreated() {
    const mvpdContainer = document.getElementById('playerControlsMvpdLogo');

    if (mvpdContainer) {
      playerStore.mvpdContainer = ref(mvpdContainer);
    }
  },
  // deprecated, not used in the plugins anymore. We should throw an error with the actual message on the plugin side instead.
  throwError() {
    throw new PlaybackError(tb('error.playbackStream.message'));
  },
  setControlsApi(api: PlayerControlsApi) {
    playerStore.controlsApi = api;
    controlsActions.setPlayerControlsApi(playerStore.controlsApi);
  },
  // this is used by some player integrations
  setQuality(
    bitrate: number | undefined,
    videoWidth: number | undefined,
    videoHeight: number | undefined
  ) {
    playerStore.bitrate = bitrate;
    playerStore.videoWidth = videoWidth;
    playerStore.videoHeight = videoHeight;
  },
  updateLiveInfo() {
    const { liveEdge = 0, loaded, player, controlsApi } = playerStore;
    const currentTime = player?.currentTime ?? 0;
    const isPlaying = loaded && !player?.paused;
    const isLive = !!controlsApi?.isLive();

    playerStore.canSeek = canSeek();
    playerStore.isLive = isLive;

    if (isLive) {
      playerStore.liveEdge = Math.max(liveEdge, currentTime);
      playerStore.isAtLiveEdge =
        isPlaying && currentTime >= playerStore.liveEdge;
    }
  },
  setChannelSchedule(
    from: string,
    until: string,
    response: ScheduleResponse | undefined
  ) {
    playerStore.channel.schedule = response
      ? {
          channelNumber: response.channel.channelNumber ?? -1,
          from,
          until,
          response,
        }
      : undefined;
  },
  setCurrentProgram(program: ScheduleType | undefined) {
    playerStore.channel.currentProgram = program;
  },
  setPlaybackSessionId(id: string) {
    playerStore.playbackSessionId = id;
  },
  reset() {
    resetStore(playerStore, initialPlayerStore, ['enteredParentalPin']);

    controlsActions.reset();
  },
  // @deprecated, but player plugins are still using it
  // leaving it here for backwards compatibility
  // after all plugins are updated remove it
  clear() {
    playerActions.reset();
  },
};

export const usePlayerStore = () => useSnapshot(playerStore) as PlayerStore;

devtoolsApi.setStore('player', playerStore, playerActions);
